import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useGetBuyerUploadedDocumentsQuery } from '@/apis/extended/admin/adminDocumentsApi';
import { rawApi } from '@/apis/rawApi';

const fmtSize = (b: number) =>
  b >= 1024 * 1024
    ? `${(b / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(b / 1024))} KB`;

const fmtDate = (v?: string | null) =>
  v
    ? new Date(v).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

async function saveBlob(path: string, filename: string) {
  const res = await rawApi.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * A buyer's uploaded documents (loan file + contract) with single and
 * package download and a checkmark once an admin has downloaded each —
 * Document Management previously only managed *requests* and had no way to
 * see or download what was uploaded.
 */
export const BuyerUploadedDocuments = ({
  userId,
  buyerName,
}: {
  userId: string;
  buyerName?: string;
}) => {
  const {
    data: docs,
    isLoading,
    error,
    refetch,
  } = useGetBuyerUploadedDocumentsQuery(userId, { skip: !userId });
  const [busy, setBusy] = useState<string | null>(null);

  const download = async (doc: {
    _id: string;
    downloadPath: string;
    fileName: string;
  }) => {
    setBusy(doc._id);
    try {
      await saveBlob(doc.downloadPath, doc.fileName);
      refetch();
    } catch (e: any) {
      // Blob error bodies need reading to show the server's message.
      let msg = 'Download failed';
      try {
        const body = JSON.parse(await e?.response?.data?.text?.());
        msg = body?.error || body?.message || msg;
      } catch {
        /* keep default */
      }
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const downloadAll = async () => {
    setBusy('all');
    try {
      const safe = (buyerName || 'buyer')
        .replace(/[^a-z0-9]+/gi, '-')
        .toLowerCase();
      await saveBlob(
        `/admin/documents/user/${userId}/package`,
        `${safe}-documents.zip`
      );
      refetch();
    } catch {
      toast.error('Could not build the document package');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Uploaded documents{docs ? ` (${docs.length})` : ''}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={!docs?.length || busy === 'all'}
          onClick={downloadAll}
          startIcon={
            busy === 'all' ? (
              <CircularProgress size={14} />
            ) : (
              <i className="fa-light fa-file-zipper" />
            )
          }
        >
          Download all (.zip)
        </Button>
      </Stack>
      {isLoading && <CircularProgress size={20} />}
      {error && (
        <Alert severity="error">Could not load uploaded documents.</Alert>
      )}
      {docs && docs.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          This buyer hasn&apos;t uploaded any documents yet.
        </Typography>
      )}
      <Stack spacing={1}>
        {docs?.map((d) => (
          <Box
            key={d._id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
            }}
          >
            <Tooltip
              title={
                d.downloadedAt
                  ? `Downloaded ${fmtDate(d.downloadedAt)}`
                  : 'Not downloaded yet'
              }
            >
              <Box
                sx={{
                  width: 20,
                  textAlign: 'center',
                  color: d.downloadedAt ? 'success.main' : 'text.disabled',
                }}
              >
                <i
                  className={
                    d.downloadedAt
                      ? 'fa-solid fa-circle-check'
                      : 'fa-light fa-circle'
                  }
                />
              </Box>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {d.title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="div"
              >
                {d.fileName} · {fmtSize(d.size)} · {d.context} · uploaded{' '}
                {fmtDate(d.uploadedAt)}
              </Typography>
            </Box>
            {d.storage === 'local' && (
              <Tooltip title="Uploaded before durable storage — may no longer be available; ask the buyer to re-upload if the download fails.">
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="Legacy"
                />
              </Tooltip>
            )}
            <Tooltip title="Download">
              <span>
                <IconButton
                  size="small"
                  disabled={busy === d._id}
                  onClick={() => download(d)}
                >
                  {busy === d._id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <i className="fa-light fa-download" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default BuyerUploadedDocuments;
